import React, { useEffect, useState } from "react";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Plus,
  ChevronDown,
  Eraser,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "use-debounce";
import { hasPermission } from "../../../utils/permissionUtils";
import { getAllBranches } from "../../../api/hrm/branches/info";
import { getAllDepartments } from "../../../api/hrm/departments/departmentInfo";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const CLASSIFICATION_OPTIONS = [
  { value: "nhan_vien_chinh_thuc", label: "Nhân viên chính thức" },
  { value: "thuc_tap_sinh", label: "Thực tập sinh" },
  { value: "nhan_vien_thoi_vu", label: "Nhân viên thời vụ" },
  { value: "gv_thinh_giang", label: "Giáo viên thỉnh giảng" },
  { value: "gv_thoi_vu", label: "Giáo viên thời vụ" },
  { value: "gv_chinh_thuc", label: "Giáo viên chính thức" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Đang làm việc" },
  { value: "inactive", label: "Ngưng sử dụng" },
];

const EmployeeFilter = ({ onNewClick, onFilterChange }) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [filters, setFilters] = useState({
    keyword: "",
    branch_id: "",
    department_id: "",
    employment_classification: "",
    status: "",
  });

  const [debouncedKeyword] = useDebounce(filters.keyword, 400);
  const isFiltering = Object.values(filters).some((v) => v !== "");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [branchRes, departmentRes] = await Promise.all([
          getAllBranches(),
          getAllDepartments(),
        ]);

        const branches = Array.isArray(branchRes)
          ? branchRes
          : Array.isArray(branchRes?.items)
          ? branchRes.items
          : Array.isArray(branchRes?.data)
          ? branchRes.data
          : [];

        const departments = Array.isArray(departmentRes)
          ? departmentRes
          : Array.isArray(departmentRes?.items)
          ? departmentRes.items
          : Array.isArray(departmentRes?.data)
          ? departmentRes.data
          : [];

        setBranchOptions(
          branches.map((b) => ({ value: b.id, label: b.name || "—" }))
        );
        setDepartmentOptions(
          departments.map((d) => ({ value: d.id, label: d.name || "—" }))
        );
      } catch (err) {
        console.error("Load employee filter options failed:", err);
        setBranchOptions([]);
        setDepartmentOptions([]);
      }
    };

    loadOptions();
  }, []);

  useEffect(() => {
    onFilterChange?.({ ...filters, keyword: debouncedKeyword });
  }, [debouncedKeyword]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = {
      keyword: "",
      branch_id: "",
      department_id: "",
      employment_classification: "",
      status: "",
    };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 ${
        active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-10 rounded-2xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="">Tất cả {label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors"
        />
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1 group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm theo tên nhân sự..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all"
            />
          </div>
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced}
            title="Lọc nâng cao"
          />
        </div>
        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc nâng cao"
          footer={
            <>
              <button
                type="button"
                onClick={clearFilters}
                className="py-4 border border-gray-200 text-gray-700 rounded-2xl font-bold flex-1"
              >
                XÓA LỌC
              </button>
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200 flex-1"
              >
                ÁP DỤNG
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <SelectBox
              label="Chi nhánh"
              value={filters.branch_id}
              options={branchOptions}
              onChange={(v) => updateFilter("branch_id", v)}
            />
            <SelectBox
              label="Bộ phận"
              value={filters.department_id}
              options={departmentOptions}
              onChange={(v) => updateFilter("department_id", v)}
            />
            <SelectBox
              label="Phân loại"
              value={filters.employment_classification}
              options={CLASSIFICATION_OPTIONS}
              onChange={(v) =>
                updateFilter("employment_classification", v)
              }
            />
            <SelectBox
              label="Trạng thái"
              value={filters.status}
              options={STATUS_OPTIONS}
              onChange={(v) => updateFilter("status", v)}
            />
          </div>
        </MobileAdvancedFilterSheet>

        {/* Thêm nhân viên: chỉ desktop — không hiện FAB trên mobile */}
      </div>
    );
  }

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder="Tìm theo tên nhân viên..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton
            icon={RotateCw}
            onClick={() => onFilterChange?.({ ...filters })}
            title="Làm mới"
          />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced}
            title="Lọc nâng cao"
          />

          {isFiltering && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2"
            >
              <Eraser size={14} /> Xóa lọc
            </button>
          )}

        </div>

        {hasPermission("hrm.employees", "C") && (
          <button
            type="button"
            onClick={onNewClick}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold hover:shadow-lg hover:shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider"
          >
            <Plus size={18} />
            Thêm nhân viên
          </button>
        )}
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-100">
              <SelectBox
                label="Chi nhánh"
                value={filters.branch_id}
                options={branchOptions}
                onChange={(v) => updateFilter("branch_id", v)}
              />
              <SelectBox
                label="Bộ phận"
                value={filters.department_id}
                options={departmentOptions}
                onChange={(v) => updateFilter("department_id", v)}
              />
              <SelectBox
                label="Phân loại"
                value={filters.employment_classification}
                options={CLASSIFICATION_OPTIONS}
                onChange={(v) => updateFilter("employment_classification", v)}
              />
              <SelectBox
                label="Trạng thái"
                value={filters.status}
                options={STATUS_OPTIONS}
                onChange={(v) => updateFilter("status", v)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmployeeFilter;
